import React from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from 'react-query'
import { Calendar, Users, Banknote, TrendingUp } from 'lucide-react'
import { format } from 'date-fns'
import { formatCurrency } from '@/utils/formatters'
import { dashboardService } from '@/services/dashboardService'

const ProjectsOverview = () => {
  // console.log('ProjectsOverview component rendered');
  
  const { data: projectsData, isLoading, error } = useQuery(
    'dashboard-projects-overview',
    () => {
      // console.log('Projects overview query function called');
      return dashboardService.getProjectsOverview();
    },
    {
      select: (response) => response.data,
      refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
      onError: (error) => {
        console.error('Projects overview fetch error:', error);
      },
      onSuccess: (data) => {
        // console.log('Projects overview fetched successfully:', data);
      }
    }
  )

  const projects = projectsData?.data?.projects || [];
  const displayProjects = projects


  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800',
      on_hold: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-green-500'
    if (progress >= 60) return 'bg-blue-500'
    if (progress >= 40) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl" style={{ border: '0.5px solid #e5e7eb' }}>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="flex items-center justify-between mb-6">
              <div className="h-6 bg-gray-300 rounded w-1/4"></div>
              <div className="h-4 bg-gray-300 rounded w-16"></div>
            </div>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="border rounded-xl p-4" style={{ borderColor: '#e5e7eb' }}>
                  <div className="h-5 bg-gray-300 rounded w-1/3 mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded w-1/4 mb-3"></div>
                  <div className="h-2 bg-gray-300 rounded w-full mb-3"></div>
                  <div className="flex space-x-4">
                    <div className="h-4 bg-gray-300 rounded w-20"></div>
                    <div className="h-4 bg-gray-300 rounded w-20"></div>
                    <div className="h-4 bg-gray-300 rounded w-20"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-2xl" style={{ border: '0.5px solid #e5e7eb' }}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">My Projects</h3>
          <Link
            to="/projects"
            className="text-sm text-[#DF6B38] hover:text-[#c85f30] font-medium flex items-center gap-1"
          >
            View all
            <span>→</span>
          </Link>
        </div>
        <div className="space-y-4">
          {displayProjects.slice(0, 3).map((project) => (
            <div
              key={project.id}
              className="border rounded-xl p-4 hover:shadow-md transition-all"
              style={{ borderColor: '#e5e7eb' }}
            >
              <div className="flex items-center justify-between mb-3">
                <Link
                  to={`/projects/${project.id}`}
                  className="text-base font-medium text-gray-900 hover:text-[#DF6B38]"
                >
                  {project.name}
                </Link>
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${getStatusColor(
                    project.status
                  )}`}
                >
                  {project.status.replace("_", " ")}
                </span>
              </div>

              {/* Progress Bar */}
              <div className="mb-3">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                  <span>Progress</span>
                  <span className="font-medium">{project.progress}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${getProgressColor(
                      project.progress
                    )}`}
                    style={{ width: `${project.progress}%` }}
                  ></div>
                </div>
              </div>

              {/* Project Details */}
              <div className="flex items-center justify-between text-xs text-gray-600">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center">
                    <Calendar className="h-3.5 w-3.5 mr-1" />
                    <span>{format(new Date(project.end_date), "MMM dd")}</span>
                  </div>
                  <div className="flex items-center">
                    <Users className="h-3.5 w-3.5 mr-1" />
                    <span>{project.team_size}</span>
                  </div>
                </div>
                <div className="flex items-center">
                  <Banknote className="h-3.5 w-3.5 mr-1" />
                  <span className="font-medium">{formatCurrency(project.budget_spent || 0)}</span>
                  <span className="text-gray-400 ml-1">
                    / {formatCurrency(project.budget_allocated || 0)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {displayProjects.length === 0 && (
          <div className="text-center py-12">
            <TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm mb-2">No projects assigned yet</p>
            <Link
              to="/projects"
              className="text-[#DF6B38] hover:text-[#c85f30] text-sm font-medium"
            >
              Browse available projects →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProjectsOverview